import { useEffect, useRef, useState } from 'react';
import type { AnalyticsEvent, PlannerAction } from '../../core/events';
import { topicTitle, type TopicPage } from '../../core/topics';
import { ApiError, call, type GetToken } from '../api';
import { isPlainClick } from '../navigation';
import { RichContent } from '@240tutoring/labs-ui/rich-content';
import '@240tutoring/labs-ui/rich-content.css';

/**
 * A published 240 topic, read in place (R12). Copied from One Step Ahead. `topic_opened` is
 * sent once the page has loaded, from the browser (KTD12), carrying the plan and unit it was
 * opened from.
 */
export function TopicReader({ id, name, getToken, onBack, onTrack, eventProperties = {} }: {
  id: string; name?: string; getToken: GetToken; onBack(): void;
  onTrack?(action: PlannerAction, properties?: AnalyticsEvent['properties']): void;
  eventProperties?: AnalyticsEvent['properties'];
}) {
  const [topic, setTopic] = useState<TopicPage | null>(null); const [error, setError] = useState(false); const [attempt, setAttempt] = useState(0);
  const trackRef = useRef(onTrack); trackRef.current = onTrack;
  const propertiesRef = useRef(eventProperties); propertiesRef.current = eventProperties;
  useEffect(() => {
    let live = true; const controller = new AbortController(); setError(false); setTopic(null);
    void call<TopicPage>(getToken, `/api/topics/${encodeURIComponent(id)}`, { signal: controller.signal })
      .then(data => { if (live) { setTopic(data); trackRef.current?.('topic_opened', { topic_id: id, ...propertiesRef.current }); } })
      .catch(failure => { if (live) { trackRef.current?.('topic_error_shown', { topic_id: id, reason: failure instanceof ApiError ? failure.reason : 'unreachable' }); setError(true); } });
    return () => { live = false; controller.abort(); };
  }, [id, getToken, attempt]);
  const title = topicTitle({ name: name ?? topic?.name ?? 'Study topic' });
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { heading.current?.focus({ preventScroll: true }); }, [id]);
  useEffect(() => { document.title = 'Study topic · 240 Planner'; }, [title]);
  return <article aria-labelledby="topic-title" className="topic-reader ph-mask">
    <a className="topic-back" href="/" onClick={event => { if (isPlainClick(event)) { event.preventDefault(); onBack(); } }}>← Back to plan</a>
    <header className="topic-reader-header"><div className="eyebrow">240 STUDY MATERIAL</div><h1 ref={heading} tabIndex={-1} id="topic-title">{title}</h1></header>
    <div className="topic-body">
      {!topic && !error && <p role="status">Loading the published study material…</p>}
      {error && <div role="alert"><p>We could not load this topic. Try again in a moment.</p><button className="secondary" onClick={() => setAttempt(n => n + 1)}>Try again</button></div>}
      {topic?.materials.map((material, i) => <RichContent key={i} content={material} />)}
      {topic?.materials.length === 0 && <p>No study material is available for this topic yet.</p>}
    </div>
  </article>;
}
